/** Shared status → badge-tone mappings (pure; client + server safe). */

import type { Database } from "@/lib/database.types";

export type Tone =
  | "neutral"
  | "good"
  | "warn"
  | "bad"
  | "info"
  | "orange"
  | "purple";

type AssetAvailability = Database["public"]["Enums"]["asset_availability"];

export function availabilityTone(availability: string): Tone {
  switch (availability) {
    case "available":
      return "good";
    case "private":
      return "info";
    case "draft":
      return "warn";
    case "damaged":
    case "lost":
      return "bad";
    case "sold":
    default:
      return "neutral";
  }
}

/** Asset condition (0–5) → human label. 5 new · 4 excellent · 3 good · 2 fair · 1 poor · 0 damaged. */
export const CONDITION_LABELS: Record<number, string> = {
  5: "new",
  4: "excellent",
  3: "good",
  2: "fair",
  1: "poor",
  0: "damaged",
};

/** Asset condition → human label (office lowercase style); falls back to the number. */
export function conditionLabel(condition: number): string {
  return CONDITION_LABELS[condition] ?? String(condition);
}

export function conditionTone(condition: number): Tone {
  if (condition >= 3) return "good";
  if (condition === 2) return "warn";
  return "bad";
}

/**
 * Minimum condition at which an asset is rentable. Below this (poor/damaged) the
 * asset is held back from availability counts, conflict checks, and assignment.
 * Single source of truth — do not inline the threshold.
 */
export const MIN_RENTABLE_CONDITION = 2;

/** Whether an asset of this `condition` is rentable (>= {@link MIN_RENTABLE_CONDITION}). */
export function isServiceable(condition: number): boolean {
  return condition >= MIN_RENTABLE_CONDITION;
}

/**
 * Availabilities the studio no longer holds. Stated as the exceptions, so an
 * availability added later is presumed to be in the building until someone
 * records it gone — the physical default. Typed against the generated enum: a
 * renamed value stops compiling here instead of quietly rejoining the count.
 */
const UNHELD_AVAILABILITY: ReadonlySet<AssetAvailability> = new Set([
  "sold",
  "lost",
]);

/**
 * Whether an asset of this availability is inventory the studio HOLDS.
 *
 * Replacement exposure is what the studio would replace, so a unit it sold
 * left on purpose and a lost one is already gone; neither is property to
 * insure. `private` is held — off the rental floor, still in the building.
 *
 * Single source of truth for that population: the stats headline, its
 * per-item itemization, and the dashboard tile all count through it, so the
 * three cannot disagree about one number. Distinct from the rentable pool
 * ({@link isServiceable} + `available`), which is a narrower question — what
 * can go out today, not what the studio owns.
 */
export function isHeldAsset(availability: string): boolean {
  return !UNHELD_AVAILABILITY.has(availability as AssetAvailability);
}

/**
 * The rentable pool is `available` + serviceable, and the asset list's pool and
 * setup filters both mean exactly that (`fetchAssets`). So naming any OTHER
 * availability beside them asks for an empty set BY DEFINITION — not a gap in
 * the data. The list says which two filters cannot overlap instead of
 * answering "no assets match" to a question it never got to ask.
 *
 * Lives here with the pool's other terms so a change to what "rentable" means
 * moves the query and the explanation together. Pass the availability that
 * actually reached the query: `fetchAssets` drops an unrecognized one, and a
 * value it ignored excludes nothing.
 */
export function poolExcludesAvailability(
  availability: string | null | undefined,
  pool: { rentable?: string | null; readiness?: string | null },
): boolean {
  const scopedToPool = pool.rentable === "yes" || pool.readiness === "missing";
  return scopedToPool && !!availability && availability !== "available";
}

/** The four item offer states in display order, each with its tone and what
 *  it means — the one table behind {@link listingState}, the items-list filter
 *  options, its heading's legend, and the stats breakdown, so a rename or
 *  re-tone lands everywhere at once. */
export const LISTING_STATES = [
  { label: "reservable", tone: "good", note: "live; self-serve reservations" },
  { label: "listed", tone: "info", note: "live; inquire only" },
  { label: "scheduled", tone: "info", note: "publish date in the future" },
  {
    label: "unlisted",
    tone: "neutral",
    note: "not offered: draft, internal, or pulled",
  },
] as const satisfies readonly { label: string; tone: Tone; note: string }[];

/** The dot a legend wears for a tone — the Badge's text colour, as a fill. */
export function toneSwatch(tone: Tone): string {
  switch (tone) {
    case "good":
      return "bg-emerald-400";
    case "warn":
      return "bg-amber-400";
    case "bad":
      return "bg-red-400";
    case "info":
      return "bg-sky-400";
    case "orange":
      return "bg-orange-400";
    case "purple":
      return "bg-purple-400";
    case "neutral":
      return "bg-fg-subtle";
  }
}

/** The Badge's text colour for a tone, for a line said in its register
 *  without the pill. */
export function toneText(tone: Tone): string {
  switch (tone) {
    case "good":
      return "text-emerald-400";
    case "warn":
      return "text-amber-400";
    case "bad":
      return "text-red-400";
    case "info":
      return "text-sky-400";
    case "orange":
      return "text-orange-400";
    case "purple":
      return "text-purple-400";
    case "neutral":
      return "text-fg-muted";
  }
}

const LISTING_STATE_BY_LABEL = Object.fromEntries(
  LISTING_STATES.map((s) => [s.label, s]),
) as Record<
  (typeof LISTING_STATES)[number]["label"],
  (typeof LISTING_STATES)[number]
>;

/**
 * Item offer state for office display, derived from the publish gate
 * (`published_at`) and the self-serve flag (`reservable`). Replaces the dropped
 * `item.availability`: unlisted = not offered (draft/internal/pulled);
 * scheduled = publish date in the future; reservable = live + self-serve;
 * listed = live but inquire-only.
 */
export function listingState(
  publishedAt: string | null,
  reservable: boolean,
): { label: string; tone: Tone } {
  if (!publishedAt) return LISTING_STATE_BY_LABEL.unlisted;
  if (new Date(publishedAt).getTime() > Date.now())
    return LISTING_STATE_BY_LABEL.scheduled;
  return reservable
    ? LISTING_STATE_BY_LABEL.reservable
    : LISTING_STATE_BY_LABEL.listed;
}

/** Reservation lifecycle status → tone. NULL = draft. */
export function reservationStatusTone(status: string | null): Tone {
  switch (status) {
    case null: // draft
      return "neutral";
    case "quoted":
      return "info";
    case "accepted":
      return "warn";
    case "confirmed":
      return "good";
    case "fulfilled": // gear out with the client
      return "info";
    case "returned":
      return "warn";
    case "settled":
    case "closed":
      return "neutral";
    case "cancelled":
      return "neutral";
    default:
      return "neutral";
  }
}

/** Quote status → tone. NULL = pending (sent, undecided); the list resolves a
 *  status-less quote to the "pending" or "stale" label first, so both the raw
 *  column value and the resolved label are accepted. Stale is inert, not
 *  live work, so it reads neutral rather than info. */
/** A claim's status on the ledger: filed and waiting reads quiet, the client
 *  accepting liability reads good, a contested claim reads bad, and a closed
 *  one is settled history. */
export function claimStatusTone(status: string): Tone {
  switch (status) {
    case "notified":
      return "info";
    case "accepted":
      return "good";
    case "disputed":
      return "bad";
    case "draft":
    case "closed":
    default:
      return "neutral";
  }
}

export function quoteStatusTone(status: string | null): Tone {
  switch (status) {
    case null: // pending
    case "pending":
      return "info";
    case "stale":
      return "neutral";
    case "accepted":
      return "good";
    case "rejected":
      return "bad";
    case "cancelled":
      return "neutral";
    default:
      return "neutral";
  }
}

/** Quote status → human label. NULL = pending. */
export function quoteStatusLabel(status: string | null): string {
  return status ?? "pending";
}

/** Locale code → human language label (lowercase, office style). */
export function languageLabel(locale: string | null | undefined): string {
  switch (locale) {
    case "en":
      return "english";
    case "es":
      return "spanish";
    default:
      return locale ?? "—";
  }
}

export function paymentKindTone(kind: string): Tone {
  // Order matters: a release/refund is net-neutral and must be matched before
  // the generic "deposit*" → info rule (deposit_release also starts with
  // "deposit"), otherwise it would wrongly read as an inflow.
  if (kind === "refund" || kind === "deposit_release") return "neutral";
  if (kind.startsWith("deposit")) return "info";
  return "good";
}
